import Button from '@allesauto/core/button';
import Headline from '@allesauto/core/headline';
import successDarkAnimation from '@allesauto/design/animations/successDark.json';
import { HeadlineVariation } from '@allesauto/utils/consts';
import clsx from 'clsx';
import React, { FC, ReactNode } from 'react';

import { DynamicAnimation } from '@/global/dynamicComponents/DynamicAnimation';

import Footer from '../../OverlayFormFooter/OverlayFormFooter';

export interface OverlaySuccessProps {
  bannerHeadline: string;
  bannerDescription?: string;
  bodyHeadline?: string;
  bodyDescription?: string;
  textLink?: React.ReactNode;
  detailsHeadline?: string;
  detailsHeadlineBig?: boolean;
  details?: React.ReactNode[];
  ctaLabel: string;
  hasFooter?: boolean;
  hasDetailsBorder?: boolean;
  isInPage?: boolean;
  onBtnClick: (() => void) | undefined;
  customDescription?: ReactNode;
  bottomText?: string;
}

const OverlaySuccess: FC<React.PropsWithChildren<OverlaySuccessProps>> = ({
  bannerHeadline,
  bannerDescription,
  bodyHeadline,
  bodyDescription,
  textLink,
  detailsHeadline,
  detailsHeadlineBig,
  details,
  ctaLabel,
  hasFooter = true,
  hasDetailsBorder = true,
  isInPage = false,
  onBtnClick,
  customDescription = undefined,
  bottomText,
}) => {
  const className = {
    container: clsx(isInPage && 'pb-40px', !isInPage && 'pb-[130px]'),
    bannerContainer: clsx(
      'bg-green60 flex flex-col justify-between pb-20px pt-60px px-15px md:flex-row',
      'monochrome:bg-grey10',
      isInPage && 'md:px-40px md:py-[55px]',
      !isInPage && 'md:p-60px'
    ),
    bannerWrapper: 'flex flex-col justify-center',
    bannerHeadline: 'font-headline text-left text-headline2 text-purple80',
    bannerDescription: 'mt-10px',
    bottomText: clsx('block font-weight-normal mb-15px mt-60px'),
    bannerAnimationContainer: 'mt-20px md:mt-0 lg:ml-20px',
    bodyContent: clsx(
      'mt-60px px-15px',
      isInPage && 'md:px-30px',
      !isInPage && 'md:px-60px'
    ),
    bodyHeadline: ' text-grey100 text-headline4 font-headline',
    bodyDescription: 'mt-10px text-grey100',
    bodyTextLink: 'mt-20px',
    detailsContainer: clsx(
      'pt-40px px-15px',
      isInPage && 'md:px-30px',
      !isInPage && 'md:px-60px'
    ),
    detailsHeadline: clsx(
      'block font-weight-bold mb-15px',
      detailsHeadlineBig && 'text-headline4 text-grey100 font-headline'
    ),
    detailWrapper: clsx(
      'flex mb-10px text-grey80 text-p1',
      hasDetailsBorder && 'border-1px border-grey20 mt-10px pl-20px py-20px'
    ),
    buttonWrapper: 'flex justify-end pt-40px px-30px w-full',
  };

  return (
    <div className={className.container}>
      <div className={className.bannerContainer}>
        <div className={className.bannerWrapper}>
          <Headline
            headlineText={bannerHeadline}
            headlineColor="text-purple80"
            headlineTag={HeadlineVariation.H2}
            descriptionText={bannerDescription}
            descriptionColor="text-grey100"
            className="sm:text-center lg:text-left monochrome:text-black"
          />
        </div>
        <div className={className.bannerAnimationContainer}>
          <DynamicAnimation
            width={160}
            height={160}
            autoplay={true}
            file={successDarkAnimation}
          />
        </div>
      </div>
      {(bodyHeadline || bodyDescription || textLink) && (
        <div className={className.bodyContent}>
          {bodyHeadline && (
            <div className={className.bodyHeadline}>{bodyHeadline}</div>
          )}
          {bodyDescription && (
            <div className={className.bodyDescription}>{bodyDescription}</div>
          )}
          {textLink && <div className={className.bodyTextLink}>{textLink}</div>}
        </div>
      )}
      {customDescription
        ? customDescription
        : (detailsHeadline || details) && (
            <div className={className.detailsContainer}>
              {detailsHeadline && (
                <span className={className.detailsHeadline}>
                  {detailsHeadline}
                </span>
              )}
              <ul>
                {details &&
                  details.length &&
                  details.map((detail, index) => {
                    return (
                      <li className={className.detailWrapper} key={index}>
                        {detail}
                      </li>
                    );
                  })}
              </ul>
            </div>
          )}
      {bottomText && (
        <div className={className.bodyContent}>
          <div className={className.bottomText}>{bottomText}</div>
        </div>
      )}
      {hasFooter && (
        <Footer
          buttonText={ctaLabel}
          onButtonClick={onBtnClick}
          className="mt-[120px]"
        />
      )}
      {!hasFooter && (
        <div className={className.buttonWrapper}>
          <Button text={ctaLabel} type="button" onClick={onBtnClick} />
        </div>
      )}
    </div>
  );
};
export default OverlaySuccess;
